import { Injectable } from '@angular/core';
import { TenantInfoService } from '@proxy/tenant-info.service';
import { AttachmentService } from '@proxy/common/attachment.service';
import { AppImages } from 'src/resources/app_images';

@Injectable({ providedIn: 'root' })
export class TenantBrandingService {
  constructor(
    private tenantInfoService: TenantInfoService,
    private attachmentService: AttachmentService
  ) {}

  getStoredLogoUrl(): string {
    const tenantName = this.getCurrentTenantName();
    const tenantLogo = localStorage.getItem('tenant_logo');

    return this.shouldUseTenantLogo(tenantName) && tenantLogo
      ? tenantLogo
      : AppImages.focaliLogo192Path;
  }

  async loadLogoUrl(): Promise<string> {
    const tenantName = this.getCurrentTenantName();

    if (!this.shouldUseTenantLogo(tenantName)) {
      return AppImages.focaliLogo192Path;
    }

    const storedLogo = localStorage.getItem('tenant_logo');
    if (storedLogo) {
      return storedLogo;
    }

    const tenant = await this.tenantInfoService.getTenantByName(tenantName).toPromise();
    const logoValue = this.getTenantLogoValue(tenant);
    const logoUrl = await this.resolveTenantLogo(logoValue);

    if (logoUrl) {
      localStorage.setItem('tenant_logo', logoUrl);
      return logoUrl;
    }

    return AppImages.focaliLogo192Path;
  }

  // Shows the tenant logo as the browser tab icon; restores the default favicon for Focali / no tenant logo
  async applyFavicon(): Promise<void> {
    this.setFavicon(this.getStoredLogoUrl());
    try {
      this.setFavicon(await this.loadLogoUrl());
    } catch {
      // keep the favicon already set
    }
  }

  private setFavicon(logoUrl: string): void {
    const link = document.querySelector<HTMLLinkElement>("link[rel~='icon']");
    if (!link) return;

    if (!link.dataset.defaultHref) {
      link.dataset.defaultHref = link.getAttribute('href') || '';
      link.dataset.defaultType = link.getAttribute('type') || '';
    }

    const isTenantLogo = !!logoUrl && logoUrl !== AppImages.focaliLogo192Path;
    if (isTenantLogo) {
      link.removeAttribute('type');
      link.href = logoUrl;
    } else {
      if (link.dataset.defaultType) link.setAttribute('type', link.dataset.defaultType);
      link.href = link.dataset.defaultHref;
    }
  }

  private getCurrentTenantName(): string | null {
    const tenantName = localStorage.getItem('__tenant') || this.getSessionTenantName();

    if (tenantName) {
      localStorage.setItem('__tenant', tenantName);
    }

    return tenantName;
  }

  private getSessionTenantName(): string | null {
    try {
      const session = JSON.parse(localStorage.getItem('abpSession') || '{}');
      return session?.tenant?.name || null;
    } catch {
      return null;
    }
  }

  private shouldUseTenantLogo(tenantName: string | null): tenantName is string {
    if (!tenantName) {
      return false;
    }

    const normalizedTenantName = tenantName.toLowerCase();
    return normalizedTenantName !== 'focal-i' && normalizedTenantName !== 'focali';
  }

  private getTenantLogoValue(tenant: any): string | undefined {
    return (
      tenant?.tenantLogo ||
      tenant?.logoUrl2 ||
      tenant?.logoUrl ||
      tenant?.extraProperties?.tenantLogo ||
      tenant?.extraProperties?.logoUrl2 ||
      tenant?.extraProperties?.logoUrl
    );
  }

  private async resolveTenantLogo(tenantLogo: string | undefined): Promise<string | null> {
    if (!tenantLogo) {
      return null;
    }

    try {
      const logo = JSON.parse(tenantLogo);
      const attachment = logo?.attachmentId
        ? await this.attachmentService.downloadById(logo.attachmentId).toPromise()
        : null;

      const finalPath = attachment?.path || logo?.filePath;
      if (!finalPath) {
        return null;
      }

      const pathParts = finalPath.split('/') as string[];
      const logoEntity = pathParts.shift();
      const fileName = pathParts.join('/');

      if (!logoEntity || !fileName) {
        return null;
      }

      const content = await this.attachmentService.downloadFile(logoEntity, fileName).toPromise();

      return content
        ? `data:${attachment?.type || logo.type || 'image/png'};base64,${content}`
        : null;
    } catch (error) {
      console.warn('[TenantBranding] Tenant logo could not be loaded', error);
      return null;
    }
  }
}
